import {
  ComponentType,
  CSSProperties,
  forwardRef,
  type JSX,
  ReactNode,
  Ref,
} from "react";
import { colors } from "../../stitches.config";
import {
  // eslint-disable-next-line no-restricted-imports
  Body as StyledBody,
  TextSize,
  // eslint-disable-next-line no-restricted-imports
  Title as StyledTitle,
} from "./__styles__/Typography";

type AllowedStyleOverrides = Omit<
  CSSProperties,
  "fontWeight" | "fontSize" | "lineHeight" | "color"
>;

const titleSizeToHtmlTag = {
  small: "h3",
  default: "h2",
  large: "h1",
} as const;

export type TitleType = "semiBold" | "regular";

export type TitleProps = {
  size: TextSize;
  type: TitleType;
  color?: keyof typeof colors;
  style?: AllowedStyleOverrides;
  children: ReactNode;
  id?: string;
  /**
   * Only set to -1 if you need to programmatically focus the
   * element (e.g. to a heading in a modal once its mounted).
   */
  tabIndex?: -1;
};

export const Title = forwardRef<HTMLHeadingElement, TitleProps>(
  (
    { size, type, color = "contentPrimary", style, children, id, tabIndex },
    ref,
  ) => {
    const css = { color: colors[color].toString() };

    return (
      <StyledTitle
        size={size}
        type={type}
        color={color}
        css={css}
        style={style}
        as={titleSizeToHtmlTag[size]}
        id={id}
        tabIndex={tabIndex}
        ref={ref}
      >
        {children}
      </StyledTitle>
    );
  },
);

export type BodyType = "emphasis" | "regular" | "underline" | "link";

export type BodyProps = {
  id?: string;
  size: TextSize;
  type: BodyType;
  color?: keyof typeof colors | "inherit";
  style?: AllowedStyleOverrides;
  as?: keyof JSX.IntrinsicElements | ComponentType<any>;
  testId?: string;
  children: ReactNode;
};

export const Body = forwardRef(
  (props: BodyProps, ref: Ref<HTMLParagraphElement>) => {
    const {
      id,
      size,
      type,
      color = "contentPrimary",
      style,
      as = "p",
      testId,
      children,
    } = props;

    const css = {
      color: color === "inherit" ? "inherit" : colors[color].toString(),
    };

    return (
      <StyledBody
        id={id}
        size={size}
        type={type}
        css={css}
        style={style}
        as={as}
        data-testid={testId}
        ref={ref}
      >
        {children}
      </StyledBody>
    );
  },
);
